import { ChevronRight, Home, Plus, Settings } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useMatch } from "react-router-dom";
import type { AccountSummary, ProjectListItem } from "../../shared/api-types";
import { useT } from "../i18n";
import { byName, findTour, useProjects } from "../lib/projects";
import { AccountSwitcher } from "./AccountSwitcher";
import { statusDotClass } from "./TourCard";

// Eine Zeile, drei Zustände: normal, überfahren, aktuelle Seite. Die aktuelle
// Seite trägt die Akzentfarbe — Grau bleibt dem Überfahren vorbehalten, sonst
// sind „hier bin ich“ und „hier zeige ich hin“ nicht zu unterscheiden.
const rowBase = "flex items-center gap-2 rounded-lg px-2 py-2 text-sm transition-colors sm:py-1.5";
const rowIdle =
  "text-zinc-600 hover:bg-zinc-200/60 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100";
const rowActive = "bg-accent/10 font-medium text-accent";

function navClass({ isActive }: { isActive: boolean }): string {
  return `${rowBase} ${isActive ? rowActive : rowIdle}`;
}

/** Aufgeklappte Projekte je Account merken — pro Browser, nicht pro Sitzung. */
function storageKey(accountId: string | null): string {
  return `fast360.sidebar.open.${accountId ?? "none"}`;
}

function readOpen(accountId: string | null): string[] {
  try {
    const raw = localStorage.getItem(storageKey(accountId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeOpen(accountId: string | null, ids: string[]): string[] {
  try {
    localStorage.setItem(storageKey(accountId), JSON.stringify(ids));
  } catch {
    // Privater Modus ohne Speicher: der Baum lebt dann nur im Arbeitsspeicher
  }
  return ids;
}

/**
 * Seitenleiste: Account oben, darunter die Navigation und der Projektbaum,
 * unten die Einstellungen. Sie ist der Rahmen der App — die Kopfzeile daneben
 * gehört zur jeweiligen Seite.
 */
export function Sidebar({
  account,
  accounts,
  canCreateAccount,
  onNewTour,
  onNavigate,
}: {
  account: AccountSummary | null;
  accounts: AccountSummary[];
  canCreateAccount: boolean;
  onNewTour: () => void;
  /** Auf dem Handy schließt jede Navigation die Schublade */
  onNavigate: () => void;
}) {
  const { projects } = useProjects();
  const t = useT();
  const tourMatch = useMatch("/tours/:id");
  const projectMatch = useMatch("/projects/:id");
  const accountId = account?.id ?? null;

  const [open, setOpen] = useState<string[]>(() => readOpen(accountId));

  // Accountwechsel lädt die Seite komplett neu; der Zustand wird trotzdem
  // sauber nachgezogen, falls das einmal nicht so ist.
  useEffect(() => setOpen(readOpen(accountId)), [accountId]);

  const activeTourId = tourMatch?.params.id ?? null;
  const activeProjectId =
    projectMatch?.params.id ??
    (activeTourId ? (findTour(projects, activeTourId)?.project.id ?? null) : null);

  // Das Projekt der offenen Seite klappt EINMAL auf. Danach bleibt es
  // zuklappbar — ein Zweig, der sich nicht schließen lässt, wirkt kaputt.
  const autoOpened = useRef<string | null>(null);
  useEffect(() => {
    if (!activeProjectId || autoOpened.current === activeProjectId) return;
    autoOpened.current = activeProjectId;
    setOpen((prev) =>
      prev.includes(activeProjectId) ? prev : writeOpen(accountId, [...prev, activeProjectId]),
    );
  }, [activeProjectId, accountId]);

  const toggle = useCallback(
    (id: string) => {
      setOpen((prev) =>
        writeOpen(accountId, prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]),
      );
    },
    [accountId],
  );

  // Der Name führt zur Projektseite und klappt dabei auf — zuklappen tut nur
  // der Pfeil. Sonst schlösse sich der Zweig genau in dem Moment, in dem man
  // ihn sehen will.
  const expand = useCallback(
    (id: string) => {
      setOpen((prev) => (prev.includes(id) ? prev : writeOpen(accountId, [...prev, id])));
    },
    [accountId],
  );

  const list = byName(projects ?? []);

  return (
    <div className="flex h-full flex-col">
      {/* h-14 wie die Kopfzeile daneben — die Marke steht auf derselben Linie */}
      <div className="flex h-14 shrink-0 items-center px-5">
        <NavLink to="/" onClick={onNavigate} className="text-[15px] font-bold tracking-tight">
          fast<span className="text-accent">360</span>
        </NavLink>
      </div>
      {(account || canCreateAccount) && (
        <div className="px-3">
          <AccountSwitcher
            active={account}
            accounts={accounts}
            canCreate={canCreateAccount}
            fullWidth
          />
        </div>
      )}

      {account && (
        <>
          {/* „Neue Tour“ ist eine Handlung, kein Ziel — deshalb ein Knopf und
              keine Navigationszeile, die eine Seite verspricht. */}
          <div className="mt-4 px-3">
            <button
              type="button"
              onClick={() => {
                onNavigate();
                onNewTour();
              }}
              className="flex h-9 w-full items-center justify-center gap-2 rounded-lg border border-zinc-200 bg-white text-sm font-medium text-zinc-700 shadow-sm transition-colors hover:bg-zinc-100 dark:border-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-200 dark:hover:bg-zinc-800"
            >
              <Plus className="size-4" />
              {t.dashboard.newTour}
            </button>
          </div>

          <nav className="mt-4 px-3">
            <NavLink to="/" end onClick={onNavigate} className={navClass}>
              {({ isActive }) => (
                <>
                  <Home
                    className={`size-4 shrink-0 ${isActive ? "text-accent" : "text-zinc-400"}`}
                  />
                  {t.app.navTours}
                </>
              )}
            </NavLink>
          </nav>
        </>
      )}

      {/* Der Baum ist der einzige Teil, der lang werden kann — nur er scrollt */}
      {account && (
        <div className="mt-5 min-h-0 flex-1 overflow-y-auto px-3 pb-4">
          <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">
            {t.nav.projects}
          </p>
          {projects === null ? (
            <p className="px-2 py-1 text-sm text-zinc-400">{t.common.loading}</p>
          ) : list.length === 0 ? (
            <p className="px-2 py-1 text-sm text-zinc-400">{t.nav.noProjects}</p>
          ) : (
            <ul className="flex flex-col gap-px">
              {list.map((project) => (
                <ProjectBranch
                  key={project.id}
                  project={project}
                  expanded={open.includes(project.id)}
                  onToggle={() => toggle(project.id)}
                  onOpenProject={() => {
                    expand(project.id);
                    onNavigate();
                  }}
                  activeProjectId={activeProjectId}
                  activeTourId={activeTourId}
                  onNavigate={onNavigate}
                />
              ))}
            </ul>
          )}
        </div>
      )}

      <div
        className={`border-t border-zinc-200 px-3 py-3 dark:border-zinc-800 ${account ? "" : "mt-auto"}`}
      >
        <NavLink to="/settings" onClick={onNavigate} className={navClass}>
          {({ isActive }) => (
            <>
              <Settings
                className={`size-4 shrink-0 ${isActive ? "text-accent" : "text-zinc-400"}`}
              />
              {t.menu.settings}
            </>
          )}
        </NavLink>
      </div>
    </div>
  );
}

/**
 * Ein Objekt mit seinen Rundgängen. Der Name führt zur Projektseite, der Pfeil
 * klappt nur auf und zu — zwei getrennte Ziele in einer Zeile, damit ein Klick
 * nie das Falsche tut.
 */
function ProjectBranch({
  project,
  expanded,
  onToggle,
  onOpenProject,
  activeProjectId,
  activeTourId,
  onNavigate,
}: {
  project: ProjectListItem;
  expanded: boolean;
  onToggle: () => void;
  onOpenProject: () => void;
  activeProjectId: string | null;
  activeTourId: string | null;
  onNavigate: () => void;
}) {
  const t = useT();
  const isActive = project.id === activeProjectId && !activeTourId;
  // Steckt die offene Tour hier drin, bleibt der zugeklappte Zweig markiert
  const holdsActiveTour = Boolean(activeTourId) && project.id === activeProjectId;

  return (
    <li>
      <div
        className={`group flex items-center rounded-lg pr-1 transition-colors ${
          isActive ? "bg-accent/10" : "hover:bg-zinc-200/60 dark:hover:bg-zinc-800"
        }`}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-label={`${project.name} — ${expanded ? t.nav.collapse : t.nav.expand}`}
          className="grid size-8 shrink-0 place-items-center text-zinc-400 transition-colors hover:text-zinc-700 sm:size-7 dark:hover:text-zinc-200"
        >
          <ChevronRight
            className={`size-3.5 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
        </button>
        <NavLink
          to={`/projects/${project.id}`}
          onClick={onOpenProject}
          title={project.name}
          className={`min-w-0 flex-1 truncate py-2 text-sm font-medium transition-colors sm:py-1.5 ${
            isActive
              ? "text-accent"
              : holdsActiveTour
                ? "text-zinc-900 dark:text-zinc-100"
                : "text-zinc-700 dark:text-zinc-300"
          }`}
        >
          {project.name}
        </NavLink>
        {/* Zugeklappt sagt die Zahl, was drinsteckt; aufgeklappt sieht man es */}
        {!expanded && project.tours.length > 0 && (
          <span
            className="shrink-0 pl-1 text-xs tabular-nums text-zinc-400 dark:text-zinc-500"
            title={t.nav.tourCount(project.tours.length)}
          >
            {project.tours.length}
          </span>
        )}
      </div>
      {expanded && (
        <ul className="ml-4 flex flex-col gap-px border-l border-zinc-200 pl-2 dark:border-zinc-800">
          {project.tours.length === 0 ? (
            <li className="px-2 py-1.5 text-xs text-zinc-400">{t.nav.noTours}</li>
          ) : (
            project.tours.map((tour) => (
              <li key={tour.id}>
                <NavLink
                  to={`/tours/${tour.id}`}
                  onClick={onNavigate}
                  title={tour.name}
                  aria-current={tour.id === activeTourId ? "page" : undefined}
                  className={`${rowBase} ${tour.id === activeTourId ? rowActive : rowIdle}`}
                >
                  <span
                    className={`size-2 shrink-0 rounded-full ${statusDotClass(tour)}`}
                    title={statusLabel(t, tour)}
                  />
                  <span className="min-w-0 truncate">{tour.name}</span>
                </NavLink>
              </li>
            ))
          )}
        </ul>
      )}
    </li>
  );
}

/** Beschriftung des Statuspunkts — dieselbe Ampel wie im Abzeichen der Kachel. */
function statusLabel(t: ReturnType<typeof useT>, tour: ProjectListItem["tours"][number]): string {
  if (tour.status !== "published") return t.dashboard.statusDraft;
  return tour.hasChanges ? t.dashboard.statusPublishedChanges : t.dashboard.statusPublished;
}
